import React from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { FONT, R, labelSx, outlineButtonSx, thinScrollSx } from "./roomTheme";

/*
 * Small layout pieces shared by the room: panel titles, announcement, video toggle,
 * loading / empty screens, catalogue details. Display only.
 */

export function PanelTitle({ children, action }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, pb: 0.75, borderBottom: `1px solid ${R.ink}` }}>
      <Typography component="h3" sx={{ ...labelSx, color: R.ink }}>
        {children}
      </Typography>
      {action}
    </Box>
  );
}

/** Auction announcement (text from the API / socket, unchanged). */
export function AnnouncementPanel({ text, maxHeight }) {
  if (!text) return null;
  return (
    <Box component="section" aria-label="Announcement" sx={{ bgcolor: R.warningBg, borderLeft: `3px solid ${R.warning}`, px: 1.5, py: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
        <CampaignOutlinedIcon sx={{ fontSize: 18, color: "#8A6D00" }} />
        <Typography sx={{ ...labelSx, color: "#5C4A00" }}>Announcement</Typography>
      </Box>
      <Typography sx={{ fontFamily: FONT, fontSize: "0.8125rem", color: R.text, whiteSpace: "pre-line", lineHeight: 1.45, maxHeight, overflowY: maxHeight ? "auto" : "visible", ...thinScrollSx }}>
        {text}
      </Typography>
    </Box>
  );
}

/** Watch / close live video (the page owns the toggle). */
export function VideoToggle({ open, onClick }) {
  return (
    <Button
      onClick={onClick}
      startIcon={<PlayCircleOutlineIcon sx={{ color: open ? "inherit" : R.danger, fontSize: "18px !important" }} />}
      sx={{ ...outlineButtonSx, minHeight: 30, py: 0, px: 1.25, whiteSpace: "nowrap" }}
    >
      {open ? "Close video" : "Watch video"}
    </Button>
  );
}

/** Full-page state (loading / no auction). */
export function RoomMessage({ loading, text }) {
  return (
    <Box sx={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", px: 3, py: 10, bgcolor: R.soft, borderTop: `1px solid ${R.line}`, borderBottom: `1px solid ${R.line}` }}>
      {loading ? (
        <Box role="status" aria-live="polite" sx={{ textAlign: "center" }}>
          <CircularProgress size={30} thickness={2} sx={{ color: R.ink }} />
          <Typography sx={{ ...labelSx, mt: 2 }}>Loading the auction room</Typography>
        </Box>
      ) : (
        <Box sx={{ textAlign: "center", maxWidth: 560 }}>
          <Typography sx={{ ...labelSx, letterSpacing: "3px" }}>Auction Room</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: { xs: "1.5rem", md: "2rem" }, lineHeight: 1.25, color: R.ink, mt: 1.5 }}>{text}</Typography>
        </Box>
      )}
    </Box>
  );
}
